// Préparer une dérogation, côté bureau (D-083, maquettes B1 et B4 ; D-137, maquettes d3 ; D-146, maquettes p1 à p3 validées) :
//  - deux façons dans la même feuille « Préparer » (D-146) : « Les prévenir » (un créneau, un message courtois « nous allons
//    saisir ce changement sur FBI », pas d'attente d'accord) ou « Demander leur accord » (la pré-demande de D-083 : 1 à 3
//    créneaux, ou l'inversion domicile / extérieur de l'aller ET du retour, chacun avec sa date, son heure et sa salle) ;
//    l'app propose d'office selon un seuil fixe (la salle seule, ou jusqu'à 1 h le même jour → « Les prévenir ») et dit
//    pourquoi ; le bureau peut toujours changer ; l'inversion est toujours une demande d'accord ;
//  - la salle vérifiée sur le planning des salles ; la raison ; le club adverse et son e-mail pris dans l'annuaire des
//    clubs (D-094), sinon lus sur la FFBB DEPUIS L'APPAREIL (modifiable) ; le coach prévenu s'il est coché ;
//    « Prévenir par Arceau » / « Envoyer par Arceau » (e-mail du serveur) ou « Par WhatsApp ou SMS » (le lien) ; « Voir le
//    message » : l'e-mail tel qu'il partira (le même gabarit que push-dispatch) ;
//  - la demande d'un coach (D-061) se reprend ici : son mot en tête, il suit chaque étape sur la fiche du match.
// Le suivi (3 ou 4 étapes) est dans derogation-track.ts. Le match ne bouge pas ici : il change quand la FFBB affiche le
// créneau (D-137 : dérogation confirmée).
import { clubStaffNames, createDerogPre, createDerogPrev, matchFfbbIds, teamMatches, teamStaffAccounts, type TeamMatch } from '../lib/api';
import { matchOpponentClub } from '../lib/ffbb';
import { directoryContact, refreshFromMatch } from '../lib/annuaire';
import { supabase } from '../lib/supabase';
import {
  REASONS, REASON_LABEL, answerUrl, checkSlots, dayShort, hm, isEmail, matchTitle, shareMessage, shortWhen, type DerogReason, type Slot, type SlotDraft,
} from '../domain/derogation';
import { prevShareMessage, proposeMode, shortPlace } from '../domain/derogation-suivi';
import { localDate } from '../domain/schedule';
import { COMPETITION_LABEL } from '../domain/ffbb';
import { derogationMail, type PreMail } from '../../supabase/functions/_shared/derogation-mail';
import { squadName, type Ctx } from './context';
import { $, $$, html, mount, openSheet, raw, toast } from './dom';
import { openShare } from './share';
import { dOf, openPrevShare, showHall } from './derogation-track';
import './derogations.css';

/** Ce qu'il faut savoir d'un match pour une pré-demande. */
export interface PreTarget {
  eventId: string; teamId: string; clubId: string; start: Date; location: string | null;
  team: string; opponent: string; isHome: boolean | null;
  /** La compétition, en mots (l'aperçu du message). */
  competition?: string | null;
}

/** Ce que la feuille reprend : la dernière adresse, des créneaux, la demande du coach (D-061 → D-137), la façon (D-146). */
export interface PrePrefill {
  email?: string | null; club?: string | null; slots?: Partial<SlotDraft>[]; note?: string | null; reason?: DerogReason;
  kind?: 'moved' | 'swap';
  /** Inversion datée : la nouvelle date de ce match et du retour. */
  swapDates?: { start?: string | null; pairStart?: string | null };
  /** La demande du coach reprise : son identifiant, qui, quand, son mot. */
  coach?: { change: string; by: string; at: string; note: string | null };
  /** D-146 : la façon choisie d'avance (« Demander leur accord » après « ça nous pose problème ») ; sinon proposée d'office. */
  mode?: 'accord' | 'prevenir';
}

const two = (n: number) => String(n).padStart(2, '0');
const hhmm = (d: Date) => `${two(d.getHours())}:${two(d.getMinutes())}`;
const ERR = (e: unknown, opp: string) => {
  const m = String((e as Error)?.message ?? '');
  if (m.includes('déjà en cours')) return 'Une dérogation est déjà en cours sur ce match : retrouve-la dans Dérogations.';
  if (m.includes('ne reçoit plus')) return `${opp} ne reçoit plus les e-mails d’Arceau : envoie le lien par WhatsApp ou SMS.`;
  if (m.includes('trop d')) return 'Dix e-mails envoyés aujourd’hui : réessaie demain, ou passe par WhatsApp ou SMS.';
  if (m.includes('adresse e-mail')) return 'Adresse e-mail invalide.';
  if (m.includes('créneau')) return 'Vérifie le créneau : à venir, et différent du match actuel.';
  if (m.includes('demande du coach')) return 'La demande du coach a déjà été reprise ou retirée.';
  if (m.includes('réservé')) return 'Réservé au bureau du club.';
  return 'Envoi impossible pour le moment.';
};

/** Les coachs de l'équipe (sauf moi) : « Marc, coach SF1 » pour la case à cocher et le suivi. */
export async function coachLabel(ctx: Ctx, m: { teamId: string; clubId: string; team: string }): Promise<string | null> {
  const [staff, names] = await Promise.all([teamStaffAccounts(m.teamId).catch(() => []), clubStaffNames(m.clubId).catch(() => new Map<string, string>())]);
  const who = staff.filter(s => s.user_id !== ctx.uid).map(s => names.get(s.user_id) ?? s.email.split('@')[0] ?? '').filter(Boolean);
  if (!who.length) return null;
  return who.length === 1 ? `${who[0]}, coach ${m.team}` : `les coachs ${m.team} (${who.join(', ')})`;
}

const slotRow = (i: number, d: Partial<SlotDraft> = {}) => html`
  <li class="call-slot" data-slot="${i}"><span class="eyebrow">Créneau ${i + 1}</span>
    <div class="call-slot-when"><input type="date" name="d${i}" value="${d.date ?? ''}" aria-label="Date du créneau ${i + 1}">
      <input type="time" name="t${i}" value="${d.time ?? ''}" aria-label="Heure du créneau ${i + 1}"></div>
    <input name="label" data-loc="${i}" class="dp-full" maxlength="200" value="${d.location ?? ''}" placeholder="Salle" aria-label="Salle du créneau ${i + 1}"></li>`;

/** B1, d3, p1, p2 : « Préparer la dérogation » : les prévenir, ou demander leur accord avant FBI. */
export function openDerogPreSheet(ctx: Ctx, m: PreTarget, onDone: () => void, prefill: PrePrefill = {}, incoming?: () => void): void {
  const opp = m.opponent;
  const title = matchTitle(m.team, opp, m.isHome);
  const clubName = ctx.clubs.find(c => c.id === m.clubId)?.name ?? ctx.club?.name ?? 'Le club';
  // Le premier créneau part du match tel qu'il est : changer l'heure ou la salle suffit (D-146 : un petit changement).
  const first: SlotDraft = { date: localDate(m.start), time: hhmm(m.start), location: m.location ?? '' };
  const base: SlotDraft = { date: '', time: hhmm(m.start), location: m.location ?? '' };
  let kind: 'moved' | 'swap' = prefill.kind === 'swap' && m.isHome !== null ? 'swap' : 'moved';
  let reason: DerogReason = prefill.reason ?? 'salle';
  let pair: TeamMatch | null = null;
  let oppClub = prefill.club ?? opp;
  let mode: 'accord' | 'prevenir' = prefill.mode ?? 'accord';
  let manual = !!prefill.mode;
  let replyTo: string | null = null;
  const teamName = (id: string) => ctx.teams.find(t => t.id === id)?.name ?? '';
  const coachNote = prefill.coach ? html`<div class="dg-coachnote"><p class="eyebrow">Demandée par ${prefill.coach.by} · ${dayShort(prefill.coach.at)}</p>
      ${prefill.coach.note ? html`<p>« ${prefill.coach.note} »</p>` : ''}</div>` : '';
  const drafts0 = prefill.slots?.length ? prefill.slots.slice(0, 3).map((d, i) => ({ ...(i ? base : first), ...d })) : [first];
  openSheet('Préparer la dérogation', html`
    <form class="form" id="f-dp" novalidate>
      <p class="muted small"><strong>${title}</strong> · actuellement le ${shortWhen(m.start.toISOString())}${m.location ? `, ${m.location}` : ''}</p>
      ${coachNote}
      ${m.isHome !== null ? html`<div class="seg" role="radiogroup" aria-label="Type de demande">
        <button type="button" role="radio" data-kind="moved" aria-pressed="${String(kind === 'moved')}">Date, heure ou salle</button>
        <button type="button" role="radio" data-kind="swap" aria-pressed="${String(kind === 'swap')}">Inverser domicile / extérieur</button></div>` : ''}
      <div class="field" id="dp-moved" ${kind === 'moved' ? '' : raw('hidden')}><span id="dp-slots-label">Le nouveau créneau</span>
        <ol class="call-slots" id="dp-slots">${drafts0.map((d, i) => slotRow(i, d))}</ol>
        <p class="dg-free" id="dp-hall" hidden></p>
        <button type="button" class="btn btn-ghost btn-sm dp-start" id="dp-add">${drafts0.length >= 2 ? '+ Un 3e créneau' : '+ Un 2e créneau'}</button></div>
      <div class="field" id="dp-swap" ${kind === 'swap' ? '' : raw('hidden')}><span>Inversion proposée à ${opp}</span><div id="dp-swap-box"><p class="muted small">Recherche du match retour…</p></div></div>
      <div class="field" id="dp-how" ${kind === 'swap' ? raw('hidden') : ''}><span>Comment ?</span>
        <div class="dg-pick dz-mode" role="radiogroup" aria-label="Comment" data-guide="dg-mode">
          <label data-mode="prevenir"><input type="radio" name="mode" value="prevenir"><span><strong>Les prévenir</strong>
            <span class="muted small">Un message courtois : « nous allons saisir ce changement sur FBI ». Pas d’attente d’accord.</span></span></label>
          <label data-mode="accord"><input type="radio" name="mode" value="accord"><span><strong>Demander leur accord</strong>
            <span class="muted small">1 à 3 créneaux ; ils répondent en un clic, sans compte.</span></span></label>
        </div>
        <p class="dz-auto" id="dp-auto"></p></div>
      <div class="field"><span>Pourquoi</span>
        <div class="chip-toggles" role="radiogroup" aria-label="Pourquoi">${REASONS.map(r => html`
          <button type="button" class="chip-toggle" role="radio" data-reason="${r}" aria-pressed="${String(r === reason)}">${REASON_LABEL[r]}</button>`)}</div></div>
      <label class="field"><span>En deux mots</span><input name="note" maxlength="200" placeholder="ex. gymnase pris par un tournoi" value="${prefill.note ?? ''}"></label>
      <div class="sheet-sec"><h4 id="dp-sec">Demander à ${opp}</h4>
        <div class="card dp-found" id="dp-club">
          <p class="eyebrow" id="dp-club-state">${prefill.email ? 'Adresse de la dernière demande' : 'Recherche sur la FFBB…'}</p>
          <span class="cn-found-name" id="dp-club-name">${oppClub}</span>
          <label class="field"><span id="dp-mail-label">E-mail du club</span><input name="email" type="email" inputmode="email" autocomplete="off" maxlength="200" value="${prefill.email ?? ''}"></label>
          <span class="muted small" id="dp-club-hint">Modifiable : mets plutôt le correspondant ou le coach si tu le connais.</span></div>
        <label class="check" id="dp-coach" hidden><input type="checkbox" name="coach" checked><span><strong id="dp-coach-name"></strong><br>
          <span class="muted small">Le message et la réponse ${dOf(opp)} s’affichent aussi sur la fiche du match.</span></span></label>
      </div>
      <p class="form-msg" role="alert"></p>
      <div class="btn-row"><button class="btn btn-primary" type="submit" id="dp-send">Envoyer par Arceau</button>
        <button type="button" class="btn btn-ghost" id="dp-share">Par WhatsApp ou SMS</button></div>
      <p class="hint"><span id="dp-hint"></span> <button type="button" class="link-x" id="dp-preview">Voir le message</button></p>
      ${incoming ? html`<p class="hint"><button type="button" class="btn btn-link btn-sm dp-incoming" id="dp-incoming">C’est ${opp} qui demande ? Enregistrer une demande reçue</button></p>` : ''}
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-dp')!;
    const msg = $(f, '.form-msg')!;
    const mail = $<HTMLInputElement>(f, 'input[name="email"]')!;
    let typed = !!prefill.email;
    mail.addEventListener('input', () => { typed = true; });

    // --- Les deux façons (D-146) : proposée d'office d'après le premier créneau, le bureau peut changer ------------------
    const readDraft = (li: Element): SlotDraft => ({
      date: $<HTMLInputElement>(li, 'input[type="date"]')!.value, time: $<HTMLInputElement>(li, 'input[type="time"]')!.value,
      location: $<HTMLInputElement>(li, '[data-loc]')!.value,
    });
    const firstSlot = (): Slot | null => {
      const li = $(f, '[data-slot]');
      if (!li) return null;
      const d = readDraft(li);
      const at = new Date(`${d.date}T${d.time}:00`);
      return d.date && d.time && !isNaN(at.getTime()) ? { starts_at: at.toISOString(), location: d.location.trim() || null } : null;
    };
    const unchanged = (s: Slot | null) => !s || (new Date(s.starts_at).getTime() === m.start.getTime() && (s.location ?? '') === (m.location ?? ''));
    const proposal = () => {
      const s = firstSlot();
      if (unchanged(s)) return { mode: 'prevenir' as const, why: '', none: true };
      if ($$(f, '[data-slot]').filter(li => !li.hidden && readDraft(li).date).length > 1 && mode === 'accord') return { mode: 'accord' as const, why: 'plusieurs créneaux proposés', none: false };
      return { ...proposeMode({ start: m.start, location: m.location }, s!), none: false };
    };
    const NAME = { prevenir: '« Les prévenir »', accord: '« Demander leur accord »' };
    const apply = () => {
      const p = proposal();
      if (!manual && kind === 'moved') mode = p.mode;
      if (kind === 'swap') mode = 'accord';
      const prev = mode === 'prevenir';
      for (const l of $$<HTMLLabelElement>(f, '.dz-mode label')) {
        const on = l.dataset.mode === mode;
        l.classList.toggle('on', on);
        $<HTMLInputElement>(l, 'input')!.checked = on;
      }
      const auto = $(f, '#dp-auto')!;
      auto.textContent = p.none ? 'Change l’heure, la date ou la salle : « Les prévenir » est proposé d’office pour un petit changement (la salle seule, ou jusqu’à 1 h le même jour).'
        : p.mode === mode ? (prev ? `Proposé d’office : ${p.why}. Tu peux changer.` : `Proposé d’office : ${p.why}. « Les prévenir », c’est pour la salle seule, ou jusqu’à 1 h le même jour.`)
        : `Tu as choisi ${NAME[mode]} ; proposé d’office : ${NAME[p.mode]} (${p.why}).`;
      // « Les prévenir » : un seul créneau.
      const rows = $$<HTMLElement>(f, '[data-slot]');
      rows.forEach((li, k) => { li.hidden = prev && k > 0; });
      const add = $<HTMLButtonElement>(f, '#dp-add')!;
      add.hidden = prev || rows.length >= 3;
      $(f, '#dp-slots-label')!.textContent = prev ? 'Le nouveau créneau' : `Créneaux proposés à ${opp} · 1 à 3`;
      $(f, '#dp-sec')!.textContent = prev ? `Prévenir ${opp}` : `Demander à ${opp}`;
      $(f, '#dp-send')!.textContent = prev ? 'Prévenir par Arceau' : 'Envoyer par Arceau';
      const from = `E-mail « ${clubName} via Arceau »${replyTo ? `, réponses à ${replyTo}` : ''}.`;
      $(f, '#dp-hint')!.textContent = prev
        ? `${from} ${oppClub} répond « Bien reçu » ou « Ça nous pose problème », sans compte ; tu saisis sur FBI tout de suite.`
        : `${from} ${oppClub} répond en un clic, sans compte ; tu peux saisir sur FBI sans attendre sa réponse.`;
    };
    for (const r of $$<HTMLInputElement>(f, 'input[name="mode"]')) r.addEventListener('change', () => {
      if (!r.checked) return;
      mode = r.value === 'prevenir' ? 'prevenir' : 'accord';
      manual = true;
      apply();
    });

    // Les réponses arrivent à l'adresse de l'admin qui envoie.
    void supabase?.auth.getSession().then(({ data }) => {
      const me = data.session?.user.email;
      if (me && !me.endsWith('@code.arceau.app')) { replyTo = me; apply(); }
    });
    void coachLabel(ctx, m).then(label => {
      if (!label) return;
      $(f, '#dp-coach-name')!.textContent = `Prévenir aussi ${label}`;
      $(f, '#dp-coach')!.hidden = false;
      // La demande vient du coach : il suit chaque étape, d'office.
      if (prefill.coach) { const c = $<HTMLInputElement>(f, 'input[name="coach"]')!; c.checked = true; c.disabled = true; }
    });
    // Le club adverse et son e-mail : l'annuaire des clubs (D-094), sinon la fiche FFBB de l'organisme lue
    // par l'appareil, qui met l'annuaire à jour au passage.
    const found = (state: 'book' | 'found' | 'none' | 'down', name?: string, email?: string | null) => {
      const ok = state === 'book' || state === 'found';
      const card = $(f, '#dp-club')!;
      card.classList.toggle('cn-found', ok);
      const st = $(f, '#dp-club-state')!;
      st.classList.toggle('cn-ok', ok);
      st.textContent = state === 'book' ? '✓ Annuaire des clubs (fiche FFBB)' : state === 'found' ? '✓ Trouvé sur la FFBB' : state === 'down' ? 'FFBB injoignable' : 'Pas trouvé sur la FFBB';
      $(f, '#dp-mail-label')!.textContent = ok && email ? 'E-mail du club (fiche FFBB)' : 'E-mail du club';
      $(f, '#dp-club-hint')!.textContent = ok && email ? 'Modifiable : mets plutôt le correspondant ou le coach si tu le connais.'
        : 'Sur la fiche FFBB du club, ou auprès de son coach. Sinon : par WhatsApp ou SMS.';
      if (name) { oppClub = name; $(f, '#dp-club-name')!.textContent = name; apply(); }
      if (email && !typed) mail.value = email;
    };
    if (!prefill.email) void (async () => {
      const book = await directoryContact(m.eventId).catch(() => null);
      if (book?.known && book.email) { found('book', book.name ?? undefined, book.email); return; }
      const ids = book ?? await matchFfbbIds(m.eventId).catch(() => null);
      if (!ids?.ffbb_match_id) { found('none'); return; }
      try {
        const o = await matchOpponentClub(ids.ffbb_match_id, ids.ffbb_engagement_id);
        if (o) found('found', o.name, o.email); else found('none');
        if (o?.org && ids.ffbb_engagement_id) {
          void refreshFromMatch(m.teamId, ids.ffbb_engagement_id, ids.ffbb_match_id, o.org, !!book?.logo).catch(() => undefined);
        }
      } catch { found('down'); }
    })();

    // La salle est-elle libre ? (planning des salles du club, D-130, tel qu'il est)
    const checkOpts = { exclude: [m.eventId], teamName, squadName: (q: string | null) => squadName(ctx, q) };
    let timer = 0;
    const later = (fn: () => void) => { clearTimeout(timer); timer = window.setTimeout(fn, 250); };
    const checkMoved = () => later(() => {
      const li = $(f, '[data-slot]');
      if (!li || m.isHome === false) return;
      const at = new Date(`${$<HTMLInputElement>(li, 'input[type="date"]')!.value}T${$<HTMLInputElement>(li, 'input[type="time"]')!.value}:00`);
      void showHall($(f, '#dp-hall'), m.clubId, $<HTMLInputElement>(li, '[data-loc]')!.value || m.location, at, checkOpts);
    });
    f.addEventListener('input', e => { if ((e.target as HTMLElement).closest('#dp-slots')) { checkMoved(); apply(); } });

    // Créneaux : 1 à 3 (demander leur accord).
    const add = $<HTMLButtonElement>(f, '#dp-add')!;
    add.addEventListener('click', () => {
      const list = $(f, '#dp-slots')!;
      const n = list.children.length;
      if (n >= 3) return;
      list.insertAdjacentHTML('beforeend', slotRow(n, base).s);
      add.textContent = n + 1 >= 2 ? '+ Un 3e créneau' : '+ Un 2e créneau';
      add.hidden = n + 1 >= 3;
      apply();
    });
    for (const b of $$<HTMLButtonElement>(f, '[data-reason]')) b.addEventListener('click', () => {
      reason = b.dataset.reason as DerogReason;
      $$(f, '[data-reason]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    });

    // Inversion (D-059, D-137) : le retour = l'autre match contre le même adversaire, de l'autre côté ; chacun avec sa
    // nouvelle date, son heure et sa salle (au départ : la date actuelle, la salle de l'autre). Toujours une demande d'accord.
    const side = (home: boolean | null) => (home ? 'chez nous' : `à ${shortPlace(opp)}`);
    const swapSlot = (key: 'a' | 'b', eyebrow: string, now: Date, home: boolean | null, loc: string | null, at?: string | null) => {
      const start = at ? new Date(at) : now;
      return html`<li class="call-slot" data-swap="${key}"><span class="eyebrow">${eyebrow}</span>
        <span class="small"><s>${dayShort(now.toISOString())} à ${hm(now.toISOString())}, ${side(home)}</s> → <strong class="nw">${side(!home)}</strong></span>
        <div class="call-slot-when"><input type="date" value="${localDate(start)}" data-cur="${localDate(now)}" aria-label="Date · ${eyebrow}">
          <input type="time" value="${hhmm(start)}" data-cur="${hhmm(now)}" aria-label="Heure · ${eyebrow}"></div>
        <input name="label" class="dp-full" maxlength="200" value="${loc ?? ''}" placeholder="Salle" aria-label="Salle · ${eyebrow}"></li>`;
    };
    const readSwap = (key: 'a' | 'b'): { slot: Slot; changed: boolean } | null => {
      const li = $(f, `[data-swap="${key}"]`);
      if (!li) return null;
      const [d, t] = $$<HTMLInputElement>(li, 'input[type="date"], input[type="time"]');
      const at = new Date(`${d!.value}T${t!.value}:00`);
      if (!d!.value || !t!.value || isNaN(at.getTime())) return null;
      return { slot: { starts_at: at.toISOString(), location: $<HTMLInputElement>(li, 'input[name="label"]')!.value.trim() || null },
        changed: d!.value !== d!.dataset.cur || t!.value !== t!.dataset.cur };
    };
    const checkSwap = () => later(() => {
      // La salle du match qui passe chez nous.
      const r = readSwap(m.isHome ? 'b' : 'a');
      if (!r) { const el = $(f, '#dp-hall-swap'); if (el) el.hidden = true; return; }
      void showHall($(f, '#dp-hall-swap'), m.clubId, r.slot.location, new Date(r.slot.starts_at),
        { ...checkOpts, exclude: [m.eventId, ...(pair ? [pair.event_id] : [])] });
    });
    const showSwap = async () => {
      const all = await teamMatches(m.teamId).catch(() => [] as TeamMatch[]);
      pair = all.find(x => x.event_id !== m.eventId && !x.events.cancelled_at && x.is_home !== null && x.is_home !== m.isHome
        && x.opponent.trim().toLowerCase() === opp.trim().toLowerCase()) ?? null;
      const isFirst = !pair || m.start.toISOString() <= pair.events.starts_at;
      const ourGym = m.isHome ? m.location : pair?.events.location ?? null;
      const theirGym = m.isHome ? pair?.events.location ?? null : m.location;
      mount($(f, '#dp-swap-box')!, html`<ol class="call-slots dg-swapdates">
          ${swapSlot('a', `Ce match · ${isFirst ? 'aller' : 'retour'}`, m.start, m.isHome, m.isHome ? theirGym : ourGym, prefill.swapDates?.start)}
          ${pair ? swapSlot('b', `L’autre match · ${isFirst ? 'retour' : 'aller'}`, new Date(pair.events.starts_at), pair.is_home, pair.is_home ? theirGym : ourGym, prefill.swapDates?.pairStart) : ''}
        </ol>
        <p class="dg-free" id="dp-hall-swap" hidden></p>
        <p class="muted small">${pair ? 'Chaque match prend la salle de l’autre ; tu proposes une date pour chacun.' : 'Pas d’autre match contre cet adversaire : seul celui-ci serait inversé.'}
          <button type="button" class="link-x" id="dp-same">Garder les mêmes dates</button></p>`);
      $(f, '#dp-same')!.addEventListener('click', () => {
        for (const i of $$<HTMLInputElement>(f, '[data-swap] input[data-cur]')) i.value = i.dataset.cur!;
        checkSwap();
      });
      $(f, '#dp-swap-box')!.addEventListener('input', checkSwap);
      checkSwap();
    };
    for (const b of $$<HTMLButtonElement>(f, '[data-kind]')) b.addEventListener('click', async () => {
      kind = b.dataset.kind === 'swap' ? 'swap' : 'moved';
      $$(f, '[data-kind]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      $(f, '#dp-moved')!.hidden = kind !== 'moved';
      $(f, '#dp-swap')!.hidden = kind !== 'swap';
      $(f, '#dp-how')!.hidden = kind !== 'moved';
      apply();
      if (kind === 'swap' && !$(f, '[data-swap]')) await showSwap();
    });
    apply();
    if (kind === 'swap') void showSwap(); else checkMoved();

    const collect = (): Parameters<typeof createDerogPre>[0] | null => {
      msg.textContent = '';
      let slots: Slot[] = [];
      let pairSlot: Slot | null = null;
      if (kind === 'moved') {
        const drafts = $$<HTMLLIElement>(f, '[data-slot]').filter(li => !li.hidden).map(readDraft);
        const r = checkSlots(drafts, { start: m.start, location: m.location });
        if ('error' in r) { msg.textContent = r.error; return null; }
        slots = r.slots;
      } else {
        const a = readSwap('a'), b = readSwap('b');
        if (a?.changed || b?.changed) {
          if ((a && new Date(a.slot.starts_at) <= new Date()) || (b && new Date(b.slot.starts_at) <= new Date())) { msg.textContent = 'Les nouvelles dates doivent être à venir.'; return null; }
          if (a) slots = [a.slot];
          pairSlot = b?.slot ?? null;
        }
      }
      const note = String(new FormData(f).get('note') ?? '').trim() || null;
      if (reason === 'autre' && !note) { msg.textContent = 'Dis en deux mots pourquoi.'; return null; }
      return {
        eventId: m.eventId, kind, pairId: kind === 'swap' ? pair?.event_id ?? null : null, slots, reason, reasonNote: note,
        opponentClub: oppClub, email: null, notifyCoach: !!prefill.coach || (!$(f, '#dp-coach')!.hidden && !!$<HTMLInputElement>(f, 'input[name="coach"]')?.checked),
        pairSlot: kind === 'swap' ? pairSlot : null, fromChange: prefill.coach?.change ?? null,
      };
    };
    const prevenir = () => kind === 'moved' && mode === 'prevenir';
    const create = async (input: NonNullable<ReturnType<typeof collect>>, email: string | null) => (prevenir()
      ? createDerogPrev({ eventId: input.eventId, slot: input.slots[0]!, reason: input.reason, reasonNote: input.reasonNote, opponentClub: input.opponentClub,
          email, notifyCoach: input.notifyCoach, fromChange: input.fromChange })
      : createDerogPre({ ...input, email }));
    const busy = (on: boolean) => $$<HTMLButtonElement>(f, '.btn-row button').forEach(b => { b.disabled = on; });
    f.addEventListener('submit', async e => {
      e.preventDefault();
      const input = collect();
      if (!input) return;
      const email = mail.value.trim().toLowerCase();
      if (!email) { msg.textContent = 'Indique l’e-mail du club, ou envoie le lien par WhatsApp ou SMS.'; mail.focus(); return; }
      if (!isEmail(email)) { msg.textContent = 'Adresse e-mail invalide.'; mail.focus(); return; }
      busy(true);
      try {
        const r = await create(input, email);
        close();
        toast(r.mail_status === 'skipped' ? 'Démo : la demande est créée, aucun e-mail ne part.'
          : prevenir() ? `${oppClub} est prévenu : tu peux saisir sur FBI` : `Demande envoyée à ${oppClub}`);
        onDone();
      } catch (err) { msg.textContent = ERR(err, oppClub); busy(false); }
    });
    $(f, '#dp-share')!.addEventListener('click', async () => {
      const input = collect();
      if (!input) return;
      busy(true);
      try {
        const r = await create(input, null);
        close();
        const url = answerUrl(location.origin, r.token);
        if (prevenir()) openPrevShare(oppClub, prevShareMessage({ club: clubName, title, oldIso: m.start.toISOString(), oldLocation: m.location, slot: input.slots[0]!, url }));
        else openShare('Envoyer le lien', `${oppClub} répond en un clic, sans compte. Envoie ce message à son bureau ou à son coach :`,
          shareMessage(clubName, title, m.start.toISOString(), undefined, url));
        onDone();
      } catch (err) { msg.textContent = ERR(err, oppClub); busy(false); }
    });
    // p3 : « Voir le message » : l'e-mail tel qu'il partira (même gabarit que push-dispatch), sans rien envoyer.
    $(f, '#dp-preview')!.addEventListener('click', () => {
      const input = collect();
      if (!input) return;
      const preview: PreMail = {
        id: '', token: '…', to: mail.value.trim().toLowerCase() || '(e-mail du club)', reply_to: replyTo, signer: ctx.profileName, reminder: false,
        club_name: clubName, timezone: null, team: m.team, opponent: m.opponent, is_home: m.isHome, competition: null, competition_name: m.competition ?? null,
        kind: input.kind, starts_at: m.start.toISOString(), location: m.location, pair_starts_at: pair?.events.starts_at ?? null, pair_location: pair?.events.location ?? null,
        slots: input.slots, reason: input.reason, reason_note: input.reasonNote, opponent_club: oppClub, direction: 'out', pair_slot: input.pairSlot ?? null,
        mode: prevenir() ? 'prevenir' : 'accord',
      };
      openMailPreview(`Le message à ${oppClub}`, preview);
    });
    $(f, '#dp-incoming')?.addEventListener('click', () => { close(); incoming?.(); });
  });
}

/** p3 : l'e-mail « <Club> via Arceau », tel qu'il arrive (au vouvoiement, réponses au membre qui envoie). */
function openMailPreview(title: string, p: PreMail): void {
  const b = derogationMail(p, location.origin);
  const [before, after] = b.text.split(b.link);
  openSheet(title, html`<div class="dz-mail">
      <dl class="dz-mail-h"><dt>De</dt><dd>${b.fromName}</dd><dt>À</dt><dd>${p.to}</dd>${p.reply_to ? html`<dt>Réponses</dt><dd>${p.reply_to}</dd>` : ''}
        <dt>Objet</dt><dd><strong>${b.subject}</strong></dd></dl>
      <div class="dz-mail-b">${before}${after !== undefined ? html`<a>${b.link}</a>${after}` : ''}<span class="dz-mail-f">${b.footer}</span></div></div>
    <p class="hint">Texte simple, au vouvoiement${p.mode === 'prevenir' ? '' : ', comme toutes les demandes d’accord'}. Le lien ouvre la page de réponse, sans compte ; il vaut jusqu’au coup d’envoi.</p>`);
}

/** Le titre et la compétition d'un match, pour les listes. */
export const competitionOf = (m: TeamMatch | undefined) => (m ? m.competition_name ?? COMPETITION_LABEL[m.competition] : null);
